import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import EmployeeFormDialog from '@/components/employees/EmployeeFormDialog';
import EmployeeCalculatorDialog from '@/components/employees/EmployeeCalculatorDialog';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  DollarSign,
  Edit,
  Calculator,
  Building2,
  FileText,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const getEnrollmentStatusClass = (status) => {
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-700';
};

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-medium text-slate-900">{value}</p>
      </div>
    </div>
  );
};

export default function EmployeeDetail() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);

  useEffect(() => {
    loadEmployeeDetail();
  }, [employeeId]);

  const loadEmployeeDetail = async () => {
    try {
      const [employeesData, employersData] = await Promise.all([
        api.entities.Employee.filter({ id: employeeId }),
        api.entities.Employer.list()
      ]);
      setEmployers(employersData || []);

      if (employeesData && employeesData.length > 0) {
        const emp = employeesData[0];
        setEmployee(emp);

        if (emp.employer_id) {
          const linkedEmployer = (employersData || []).find((e) => e.id === emp.employer_id);
          setEmployer(linkedEmployer || null);
        }
      } else {
        toast.error('Employee not found');
      }
    } catch (err) {
      console.error('Failed to load employee:', err);
      toast.error('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSaved = () => {
    setEditDialogOpen(false);
    loadEmployeeDetail();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/EmployeeManagement')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employees
        </Button>
        <Card>
          <CardContent className="p-12 text-center text-slate-500">
            Employee not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Button variant="outline" onClick={() => navigate('/EmployeeManagement')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employees
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setCalcDialogOpen(true)}>
            <Calculator className="w-4 h-4 mr-2" />
            Employee Calculator
          </Button>
          <Button onClick={() => setEditDialogOpen(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Employee
          </Button>
        </div>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
              {employee.first_name?.[0]}{employee.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-slate-900">
                {employee.first_name} {employee.last_name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{employee.user_email}</span>
                {employer && (
                  <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{employer.company_name}</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className={employee.profile_confirmed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {employee.profile_confirmed ? 'Confirmed' : 'Pending'}
              </Badge>
              <Badge className={getEnrollmentStatusClass(employee.enrollment_status)}>
                {employee.enrollment_status || 'Pending'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-500" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <InfoRow icon={Mail} label="Email" value={employee.user_email} />
            <InfoRow icon={Phone} label="Phone" value={employee.phone} />
            <InfoRow
              icon={Calendar}
              label="Date of Birth"
              value={employee.date_of_birth ? format(new Date(employee.date_of_birth), 'MMM d, yyyy') : null}
            />
            <InfoRow icon={User} label="Gender" value={employee.gender} />
            <InfoRow
              icon={MapPin}
              label="Address"
              value={[employee.address, employee.city, employee.state, employee.zip_code].filter(Boolean).join(', ') || null}
            />
            <InfoRow icon={FileText} label="SSN (last 4)" value={employee.ssn_last_four ? `•••-••-${employee.ssn_last_four}` : null} />
          </CardContent>
        </Card>

        {/* Employment Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-500" />
              Employment Information
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <InfoRow icon={Building2} label="Employer" value={employer?.company_name} />
            <InfoRow icon={Briefcase} label="Job Title" value={employee.job_title} />
            <InfoRow icon={Briefcase} label="Department" value={employee.department} />
            <InfoRow
              icon={User}
              label="Employment Status"
              value={employee.employment_status?.replace(/_/g, ' ')}
            />
            <InfoRow
              icon={Calendar}
              label="Hire Date"
              value={employee.hire_date ? format(new Date(employee.hire_date), 'MMM d, yyyy') : null}
            />
          </CardContent>
        </Card>

        {/* Compensation & Tax */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-500" />
              Compensation & Tax
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <InfoRow
              icon={DollarSign}
              label="Annual Salary"
              value={Number(employee.annual_salary) > 0 ? `$${Number(employee.annual_salary).toLocaleString()}` : null}
            />
            <InfoRow icon={FileText} label="Filing Status" value={employee.filing_status} />
            <InfoRow icon={User} label="Dependents" value={employee.dependents ?? null} />
            <InfoRow icon={FileText} label="W-4 Version" value={employee.w4_version?.replace('_', '-')} />
            <InfoRow icon={FileText} label="W-4 Allowances" value={employee.w4_allowances ?? null} />
            <InfoRow
              icon={DollarSign}
              label="Pay Frequency"
              value={employee.pay_frequency?.replace(/_/g, ' ')}
            />
          </CardContent>
        </Card>

        {/* Benefits & Enrollment */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Benefits & Enrollment
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <InfoRow
              icon={DollarSign}
              label="Flex Allocation"
              value={Number(employee.flex_allocation) > 0 ? `$${Number(employee.flex_allocation).toFixed(2)}/mo` : null}
            />
            <InfoRow
              icon={DollarSign}
              label="Post-Tax Deduction"
              value={Number(employee.post_tax_deduction) > 0 ? `$${Number(employee.post_tax_deduction).toFixed(2)}/mo` : null}
            />
            <InfoRow
              icon={DollarSign}
              label="Optimal Flex Amount"
              value={Number(employee.optimal_flex_amount) > 0 ? `$${Number(employee.optimal_flex_amount).toFixed(2)}/mo` : null}
            />
            <InfoRow
              icon={DollarSign}
              label="Calculated Tax Savings"
              value={Number(employee.calculated_tax_savings) > 0 ? `$${Number(employee.calculated_tax_savings).toLocaleString()}/yr` : null}
            />
            <InfoRow
              icon={FileText}
              label="Enrollment Status"
              value={employee.enrollment_status?.replace(/_/g, ' ')}
            />
          </CardContent>
        </Card>
      </div>

      <EmployeeFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        employee={employee}
        employers={employers}
        onSaved={handleEmployeeSaved}
      />

      <EmployeeCalculatorDialog
        open={calcDialogOpen}
        onOpenChange={setCalcDialogOpen}
        employee={employee}
      />
    </div>
  );
}
